import React, { RefObject, useEffect, useRef, useState } from 'react';

import { ReactComponent as SearchIcon } from 'assets/icons/ic_search.svg';
import { ContractPeriodModal } from 'features/home';
import { useSearchParams } from 'react-router-dom';
import { convertRequestToRoomType, convertRoomTypeToRequest } from 'shared/lib';
import { useWhereSearchStore } from 'store/useWhereSearchStore';
import { twMerge } from 'tailwind-merge';

import BudgetModal from './BudgetModal';
import GuestModal from './GuestModal';
import MoveInModal from './MoveInModal';
import RoomTypeModal from './RoomTypeModal';
import { roomTypeList, searchType } from './SearchContainer';
import SearchWrapper from './SearchWrapper';
import WhereModal from './WhereModal';

export interface SearchParams {
  originalWhere?: string;
  whereType?: string;
  whereValue?: string;
  moveIn?: string;
  contractPeriod?: string;
  roomTypes?: string;
  minBudget?: number;
  maxBudget?: number;
}

interface SearchBoxProps {
  withBg?: boolean;
  withBudget?: boolean;
  budgetList?: Record<string, number>;
  active: searchType;
  setActive: (value: searchType) => void;
  onSearch: ({
    originalWhere,
    whereType,
    whereValue,
    moveIn,
    contractPeriod,
    roomTypes,
    minBudget,
    maxBudget,
  }: SearchParams) => void;
}

const DEFAULT_CONTRACT_PERIOD = '12';

const SearchBox = ({ withBg, withBudget, budgetList, active, setActive, onSearch }: SearchBoxProps) => {
  const searchBoxRef = useRef<HTMLDivElement>(null);
  const [searchParams] = useSearchParams();
  const [originalWhere, setOriginalWhere] = useState('');
  const [whereType, setWhereType] = useState('');
  const [whereValue, setWhereValue] = useState('');
  const [moveIn, setMoveIn] = useState('');
  const [contractPeriod, setContractPeriod] = useState(DEFAULT_CONTRACT_PERIOD);
  const [displayPeriod, setDisplayPeriod] = useState('');
  const [roomTypes, setRoomTypes] = useState<string[]>(roomTypeList);
  const [minBudget, setMinBudget] = useState<number | undefined>(undefined);
  const [maxBudget, setMaxBudget] = useState<number | undefined>(undefined);

  const { addWhereSearch } = useWhereSearchStore();

  const handleChangePeriod = (value: number) => {
    setContractPeriod(value.toString());
    setDisplayPeriod(`${value} Months`);
  };

  useEffect(() => {
    if (searchParams.has('originalWhere')) {
      setOriginalWhere(searchParams.get('originalWhere') as string);
    }
    if (searchParams.has('whereType')) {
      setWhereType(searchParams.get('whereType') as string);
    }
    if (searchParams.has('whereValue')) {
      setWhereValue(searchParams.get('whereValue') as string);
    }
    if (searchParams.has('moveIn')) {
      setMoveIn(searchParams.get('moveIn') as string);
    }
    if (searchParams.has('contractPeriod')) {
      setContractPeriod(searchParams.get('contractPeriod') as string);
      setDisplayPeriod(`${searchParams.get('contractPeriod')} Months`);
    }
    if (searchParams.has('roomTypes')) {
      setRoomTypes(
        searchParams
          .get('roomTypes')
          ?.split(',')
          .map((type) => convertRequestToRoomType(type)) ?? [],
      );
    }
    if (searchParams.has('minBudget')) {
      setMinBudget(Number(searchParams.get('minBudget')));
    }
    if (searchParams.has('maxBudget')) {
      setMaxBudget(Number(searchParams.get('maxBudget')));
    }
  }, [searchParams]);

  useEffect(() => {
    if (active === null) {
      if ((whereType === '' || whereValue === '') && originalWhere !== '') {
        setOriginalWhere('');
      }
    }
  }, [active, whereType, whereValue, originalWhere]);

  const handleSearch = () => {
    window.dataLayer?.push({
      event: 'search',
      where: originalWhere === '' ? undefined : originalWhere,
      move_in: moveIn === '' ? undefined : moveIn,
      contract_period: contractPeriod === '' ? undefined : contractPeriod,
      room_type: roomTypes.length === 0 ? '' : roomTypes.map((type) => convertRoomTypeToRequest(type)).join(','),
      lowest_rent: minBudget === undefined ? undefined : minBudget,
      highest_rent: maxBudget === undefined ? undefined : maxBudget,
      search_term: originalWhere === '' ? undefined : originalWhere,
      type: withBg ? 'listing page' : 'main page',
      guest: '1',
    });

    onSearch({
      originalWhere: originalWhere ?? undefined,
      whereType: whereType ?? undefined,
      whereValue: whereValue ?? undefined,
      moveIn: moveIn ?? undefined,
      contractPeriod: contractPeriod ?? undefined,
      roomTypes: roomTypes.length === 0 ? undefined : roomTypes.map((type) => convertRoomTypeToRequest(type)).join(','),
      minBudget: minBudget,
      maxBudget: maxBudget,
    });

    if (!!whereType && !!whereValue && !!originalWhere) {
      addWhereSearch({ id: Date.now(), whereType, whereValue, originalValue: originalWhere });
    }
  };

  return (
    <div
      ref={searchBoxRef}
      className={twMerge(
        'static z-10 mx-auto flex h-[108px] w-fit items-center justify-center gap-x-[35px] rounded-full bg-white px-10 py-6.25 shadow-search',
        active && 'z-20',
      )}
    >
      <div className="flex h-full items-center gap-x-[25px]">
        <SearchWrapper
          width="w-[111px]"
          subWidth="w-[810px]"
          title="Where"
          onDeleteAll={() => {
            setOriginalWhere('');
            setWhereType('');
            setWhereValue('');
          }}
          isActive={active === 'WHERE'}
          setActive={() => setActive('WHERE')}
          onClose={() => {
            if (whereType === '' || whereValue === '') {
              setOriginalWhere('');
            }
            setActive(null);
          }}
          value={originalWhere}
          onChange={(e) => {
            setOriginalWhere(e.target.value);
            setWhereValue('');
            setWhereType('');
          }}
          placeholder="Search Places"
          closePosition="right-10 top-[30px]"
          position="-left-[23px]"
          ignoreRefs={[searchBoxRef as RefObject<HTMLDivElement>]}
          modalContent={
            <WhereModal
              originalValue={originalWhere}
              setOriginalValue={setOriginalWhere}
              setWhereValue={setWhereValue}
              setWhereType={setWhereType}
              onClose={() => setActive(null)}
            />
          }
        />
        <SearchWrapper
          width="w-[110px]"
          subWidth="w-[391px]"
          title="Move-in"
          onDeleteAll={() => {
            setMoveIn('');
          }}
          isActive={active === 'MOVEIN'}
          setActive={() => setActive('MOVEIN')}
          onClose={() => setActive(null)}
          value={moveIn}
          readOnly
          placeholder="Add date"
          closePosition="right-5 top-5"
          ignoreRefs={[searchBoxRef as RefObject<HTMLDivElement>]}
          modalContent={<MoveInModal value={moveIn} setValue={setMoveIn} onClose={() => setActive(null)} />}
        />

        <ContractPeriodModal
          setActive={setActive}
          isActive={active === 'PERIOD'}
          currentPeriod={Number(contractPeriod) === 0 ? 1 : Number(contractPeriod)}
          onClose={(month: number) => {
            setContractPeriod(month.toString());
            setDisplayPeriod(`${month} Months`);
            setActive(null);
          }}
          handleChangePeriod={handleChangePeriod}
        >
          <div className="flex w-[140px] flex-col gap-y-2">
            <span className="text-c2">Contract Period</span>
            <button type="button" className="border-none bg-transparent text-left text-c2 text-gray-40 outline-none">
              {displayPeriod ? displayPeriod : 'Enter numbers'}
            </button>
          </div>
        </ContractPeriodModal>

        <SearchWrapper
          width="w-[85px]"
          subWidth="w-[355px]"
          title="Guest"
          isActive={active === 'GUEST'}
          setActive={() => setActive('GUEST')}
          onClose={() => setActive(null)}
          value={'1 person'}
          placeholder="1 person"
          readOnly
          closePosition="right-5 top-[30px]"
          position="-right-12"
          ignoreRefs={[searchBoxRef as RefObject<HTMLDivElement>]}
          modalContent={<GuestModal withBg={withBg} />}
        />
        <SearchWrapper
          width="w-[134px]"
          subWidth="w-[356px]"
          title="Room Type"
          isActive={active === 'ROOM'}
          setActive={() => setActive('ROOM')}
          onClose={() => setActive(null)}
          value={
            roomTypes.length === 0
              ? ''
              : roomTypes.length === 1
                ? roomTypes[0]
                : roomTypes.length === roomTypeList.length
                  ? 'All'
                  : `${roomTypes.length} Selected`
          }
          readOnly
          placeholder="Select room type"
          closePosition="right-5 top-5"
          position="right-[111px]"
          ignoreRefs={[searchBoxRef as RefObject<HTMLDivElement>]}
          modalContent={<RoomTypeModal roomType={roomTypes} setRoomType={setRoomTypes} />}
        />

        {withBudget && (
          <SearchWrapper
            width="w-[120px]"
            subWidth="w-[504px]"
            title="Budget"
            isActive={active === 'BUDGET'}
            setActive={() => setActive('BUDGET')}
            onClose={() => setActive(null)}
            readOnly
            value={minBudget || maxBudget ? `${minBudget} - ${maxBudget}` : ''}
            placeholder="Not set"
            position="right-[100px]"
            closePosition="right-[35px] top-5"
            ignoreRefs={[searchBoxRef as RefObject<HTMLDivElement>]}
            modalContent={
              <BudgetModal
                budgetList={budgetList}
                setMinBudget={setMinBudget}
                setMaxBudget={setMaxBudget}
                minBudget={minBudget}
                maxBudget={maxBudget}
              />
            }
          />
        )}
      </div>
      <button className="rounded-full bg-primary p-3 text-white" onClick={handleSearch}>
        <SearchIcon width={31} height={31} />
      </button>
    </div>
  );
};

export default SearchBox;
